import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Rocket, CheckCircle, ArrowRight, Utensils,
  BarChart3, Smartphone, Clock, ShieldCheck, Zap,
  MessageCircle, Users, TrendingUp, Star, Play,
  ChefHat, CreditCard, Bell, Target, Gift, Headphones } from
"lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { usePlatformConfig } from "@/hooks/usePlatformConfig";

const BENEFITS = [
{ icon: Smartphone, title: "Cardápio Digital", desc: "QR Code na mesa, link no Instagram. Seus clientes pedem direto pelo celular." },
{ icon: BarChart3, title: "Relatórios em Tempo Real", desc: "Dashboard completo com faturamento, ticket médio e produtos mais vendidos." },
{ icon: Clock, title: "Pronto em 5 minutos", desc: "Configure seu cardápio e comece a receber pedidos hoje mesmo." },
{ icon: ShieldCheck, title: "Zero Taxas por Pedido", desc: "Diferente do iFood, você paga apenas a mensalidade fixa. Sem surpresas." },
{ icon: ChefHat, title: "Painel Cozinha (KDS)", desc: "Pedidos organizados por prioridade. Agilize a produção da sua cozinha." },
{ icon: Gift, title: "Fidelidade & Cupons", desc: "Programa de pontos, cashback e cupons automáticos para fidelizar clientes." }];


const STEPS = [
{ num: "01", title: "Cadastre-se grátis", desc: "Crie sua conta em menos de 2 minutos. Sem cartão de crédito." },
{ num: "02", title: "Monte seu cardápio", desc: "Adicione produtos com fotos, preços e categorias. Use IA para gerar descrições." },
{ num: "03", title: "Compartilhe o link", desc: "Divulgue nas redes sociais, coloque QR Code nas mesas e comece a vender." }];


const TESTIMONIALS = [
{ name: "Maria Rodrigues", biz: "Pizzaria Bella Napoli", text: "Em 2 semanas já estava recebendo 30+ pedidos/dia pelo cardápio digital. Muito prático!", avatar: "MR" },
{ name: "Carlos Santos", biz: "Smash Burger House", text: "Saí do iFood e economizei mais de R$2.000/mês em taxas. O investimento se paga no primeiro dia.", avatar: "CS" },
{ name: "Ana Luísa", biz: "Doce Encanto Confeitaria", text: "O suporte responde em minutos e o sistema é muito intuitivo. Meus clientes adoram!", avatar: "AL" }];


const COMPARISONS = [
{ feature: "Taxa por pedido", us: "0%", them: "12-27%" },
{ feature: "Mensalidade", us: "A partir de R$49", them: "Grátis*" },
{ feature: "Dados dos clientes", us: "100% seus", them: "Do marketplace" },
{ feature: "Personalização", us: "Total", them: "Limitada" },
{ feature: "Programa fidelidade", us: "Incluído", them: "Não tem" },
{ feature: "Suporte humanizado", us: "WhatsApp direto", them: "Bot / Ticket" }];


const LandingAds = () => {
  const { config } = usePlatformConfig();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    restaurant_name: ""
  });
  const [loading, setLoading] = useState(false);

  const utmSource = searchParams.get("utm_source") || "";
  const utmMedium = searchParams.get("utm_medium") || "";
  const utmCampaign = searchParams.get("utm_campaign") || "";
  const utmTerm = searchParams.get("utm_term") || "";
  const utmContent = searchParams.get("utm_content") || "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.restaurant_name) {
      toast({ title: "Preencha todos os campos obrigatórios", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await supabase.from("page_visits").insert({
        page_path: "/ads-lead",
        utm_source: utmSource || null,
        utm_medium: utmMedium || null,
        utm_campaign: utmCampaign || null,
        utm_term: utmTerm || null,
        utm_content: utmContent || null,
        session_id: sessionStorage.getItem("_vm_sid") || crypto.randomUUID(),
        device_type: /mobile|iphone|android/i.test(navigator.userAgent) ? "mobile" : "desktop",
        browser: navigator.userAgent.includes("Chrome") ? "Chrome" : navigator.userAgent.includes("Safari") ? "Safari" : "Outro"
      } as any);

      const params = new URLSearchParams();
      params.set("restaurant_name", formData.restaurant_name);
      params.set("full_name", formData.name);
      params.set("email", formData.email);
      if (formData.phone) params.set("phone", formData.phone);
      if (utmSource) params.set("utm_source", utmSource);
      if (utmMedium) params.set("utm_medium", utmMedium);
      if (utmCampaign) params.set("utm_campaign", utmCampaign);
      navigate(`/cadastro?${params.toString()}`);
    } catch {
      toast({ title: "Erro ao processar", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const openWhatsApp = () => {
    const whatsappNumber = config.contact_whatsapp?.replace(/\D/g, "") || "";
    const text = encodeURIComponent(
      `Olá! Vim pelo anúncio${utmSource ? ` (${utmSource})` : ""} e quero saber mais sobre o ${config.site_name} para meu restaurante "${formData.restaurant_name || ""}".`
    );
    window.open(`https://wa.me/${whatsappNumber}?text=${text}`, "_blank");
  };

  const scrollToForm = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* ── Sticky Top Bar ── */}
      <div className="sticky top-0 z-50 bg-primary text-primary-foreground py-2 text-center text-sm font-medium">
        <div className="container flex items-center justify-center gap-2">
          <Zap className="h-4 w-4" />
          <span><strong>30 dias grátis</strong> + configuração assistida</span>
          <Button
            size="sm"
            variant="secondary"
            className="ml-3 h-7 text-xs font-bold px-3"
            onClick={scrollToForm}>
            
            Quero testar
          </Button>
        </div>
      </div>

      {/* ── Hero Section ── */}
      <section className="relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-accent/5 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/3" />

        <div className="relative max-w-6xl mx-auto px-4 pt-10 pb-16 lg:pt-16 lg:pb-24">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left - Hero Copy */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}>
              
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 bg-primary/10 text-primary border border-primary/20 px-4 py-2 rounded-full text-xs font-bold mb-8 backdrop-blur-sm">
                
                <Rocket className="h-4 w-4" />
                Mais de 500 restaurantes já usam
              </motion.div>

              <h1 className="text-4xl lg:text-[3.4rem] font-display font-extrabold leading-[1.1] mb-6 tracking-tight">
                Pare de pagar{" "}
                <span className="relative">
                  <span className="text-primary">taxas absurdas</span>
                  <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 200 8" fill="none">
                    <path d="M1 5.5C40 2 80 2 100 3.5C120 5 160 6 199 3" stroke="hsl(var(--primary))" strokeWidth="2" strokeLinecap="round" opacity="0.4" />
                  </svg>
                </span>{" "}
                para marketplaces
              </h1>

              <p className="text-lg lg:text-xl text-muted-foreground mb-8 max-w-lg leading-relaxed">
                Tenha seu próprio cardápio digital com pedidos online, gestão completa e
                <strong className="text-foreground"> zero comissão por pedido</strong>.
              </p>

              {/* Counter strip */}
              <div className="flex gap-8 mb-8 pb-8 border-b border-border">
                {[
                { value: "500+", label: "Restaurantes" },
                { value: "50k+", label: "Pedidos/mês" },
                { value: "R$2M+", label: "Economizados" }].
                map((s, i) =>
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.1 }}>
                  
                    <p className="text-2xl lg:text-3xl font-display font-extrabold text-primary">{s.value}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
                  </motion.div>
                )}
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {[
                "Sem fidelidade",
                "Cancele quando quiser",
                "Suporte via WhatsApp",
                "Sem cartão de crédito"].
                map((t) =>
                <div key={t} className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                    {t}
                  </div>
                )}
              </div>
            </motion.div>

            {/* Right - Form Card */}
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="relative">
              
              {/* Glow behind card */}
              <div className="absolute -inset-4 bg-primary/10 rounded-3xl blur-2xl opacity-50" />

              <div className="relative bg-card border border-border rounded-2xl p-7 lg:p-9 shadow-2xl">
                {/* Urgency badge */}
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground text-xs font-bold px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap">
                    ⚡ Vagas limitadas esta semana
                  </span>
                </div>

                <div className="text-center mb-7 mt-3">
                  <h2 className="text-2xl font-display font-extrabold mb-1.5">Comece agora grátis</h2>
                  <p className="text-sm text-muted-foreground">30 dias grátis • Sem cartão de crédito</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Seu nome *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="João Silva"
                      className="mt-1.5 h-12 text-base"
                      required />
                    
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">E-mail *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                      placeholder="joao@email.com"
                      className="mt-1.5 h-12 text-base"
                      required />
                    
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">WhatsApp</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                        placeholder="(11) 99999-9999"
                        className="mt-1.5 h-12 text-base" />
                      
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Restaurante *</Label>
                      <Input
                        value={formData.restaurant_name}
                        onChange={(e) => setFormData((prev) => ({ ...prev, restaurant_name: e.target.value }))}
                        placeholder="Nome do local"
                        className="mt-1.5 h-12 text-base"
                        required />
                      
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-13 text-base font-bold bg-gradient-primary hover:opacity-90 transition-opacity shadow-lg"
                    disabled={loading}
                    style={{ height: '52px' }}>
                    
                    {loading ? "Processando..." :
                    <>
                        Criar meu cardápio grátis
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    }
                  </Button>

                  <div className="relative my-2">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-card px-3 text-muted-foreground">ou fale com um consultor</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11 text-sm font-semibold border-2 hover:bg-accent/5"
                    onClick={openWhatsApp}>
                    
                    <MessageCircle className="mr-2 h-4 w-4 text-primary" />
                    Conversar no WhatsApp
                  </Button>

                  <p className="text-[10px] text-center text-muted-foreground pt-1">
                    Ao se cadastrar, você concorda com nossos{" "}
                    <a href="/termos" className="underline hover:text-foreground">Termos</a> e{" "}
                    <a href="/privacidade" className="underline hover:text-foreground">Privacidade</a>.
                  </p>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Comparison Table ── */}
      <section className="py-16 lg:py-20 bg-secondary/30">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12">
            
            <span className="inline-block text-xs font-bold text-primary uppercase tracking-widest mb-3">Comparativo</span>
            <h2 className="text-2xl lg:text-4xl font-display font-extrabold mb-3">
              {config.site_name} vs Marketplaces
            </h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              Veja por que centenas de restaurantes estão migrando
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-card border border-border rounded-2xl overflow-hidden shadow-lg">
            
            {/* Header */}
            <div className="grid grid-cols-3 bg-muted/50 border-b border-border">
              <div className="p-4 text-sm font-semibold text-muted-foreground" />
              <div className="p-4 text-center">
                <span className="inline-flex items-center gap-1.5 text-sm font-bold text-primary">
                  <Utensils className="h-4 w-4" />
                  {config.site_name}
                </span>
              </div>
              <div className="p-4 text-center text-sm font-semibold text-muted-foreground">
                iFood / Rappi
              </div>
            </div>

            {COMPARISONS.map((row, i) =>
            <div key={row.feature} className={`grid grid-cols-3 ${i < COMPARISONS.length - 1 ? 'border-b border-border' : ''}`}>
                <div className="p-4 text-sm font-medium">{row.feature}</div>
                <div className="p-4 text-center text-sm font-bold text-primary">{row.us}</div>
                <div className="p-4 text-center text-sm text-muted-foreground">{row.them}</div>
              </div>
            )}
          </motion.div>

          <p className="text-[11px] text-muted-foreground text-center mt-3">
            * Marketplaces cobram comissões de 12% a 27% sobre cada pedido
          </p>

          {/* ── Calculadora de Taxas ── */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-14">
            
            <h3 className="text-xl lg:text-2xl font-display font-extrabold text-center mb-2">
              💸 Simulação real: quanto você perde em taxas?
            </h3>
            <p className="text-sm text-muted-foreground text-center mb-8 max-w-lg mx-auto">
              Veja o impacto de <strong className="text-foreground">um único produto de R$ 20,00</strong> vendido 1x por dia:
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Marketplace */}
              <div className="bg-destructive/5 border-2 border-destructive/20 rounded-2xl p-6 relative overflow-hidden">
                <div className="absolute top-3 right-3 bg-destructive text-destructive-foreground text-[10px] font-bold px-2 py-0.5 rounded-full">
                  PREJUÍZO
                </div>
                <h4 className="font-display font-bold text-lg text-destructive mb-4">📍 No Marketplace (iFood/Rappi)</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center py-2 border-b border-destructive/10">
                    <span className="text-muted-foreground">Preço do produto</span>
                    <span className="font-semibold">R$ 20,00</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-destructive/10">
                    <span className="text-muted-foreground">Comissão (27%)</span>
                    <span className="font-bold text-destructive">- R$ 5,40</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-destructive/10">
                    <span className="text-muted-foreground">Você recebe</span>
                    <span className="font-semibold">R$ 14,60</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-destructive/10">
                    <span className="text-muted-foreground">Perda mensal (30 vendas)</span>
                    <span className="font-bold text-destructive">- R$ 162,00</span>
                  </div>
                  <div className="flex justify-between items-center py-2 bg-destructive/10 rounded-lg px-3 -mx-3">
                    <span className="font-bold">Perda anual (365 vendas)</span>
                    <span className="font-extrabold text-destructive text-lg">- R$ 1.971,00</span>
                  </div>
                </div>
              </div>

              {/* Plataforma */}
              <div className="bg-primary/5 border-2 border-primary/20 rounded-2xl p-6 relative overflow-hidden">
                <div className="absolute top-3 right-3 bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5 rounded-full">
                  ECONOMIA
                </div>
                <h4 className="font-display font-bold text-lg text-primary mb-4">✅ No {config.site_name}</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center py-2 border-b border-primary/10">
                    <span className="text-muted-foreground">Preço do produto</span>
                    <span className="font-semibold">R$ 20,00</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-primary/10">
                    <span className="text-muted-foreground">Comissão por pedido</span>
                    <span className="font-bold text-primary">R$ 0,00</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-primary/10">
                    <span className="text-muted-foreground">Você recebe</span>
                    <span className="font-semibold">R$ 20,00</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-primary/10">
                    <span className="text-muted-foreground">Mensalidade fixa</span>
                    <span className="font-semibold">R$ 49,00/mês</span>
                  </div>
                  <div className="flex justify-between items-center py-2 bg-primary/10 rounded-lg px-3 -mx-3">
                    <span className="font-bold">Custo anual total</span>
                    <span className="font-extrabold text-primary text-lg">R$ 588,00</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Economia destaque */}
            <div className="mt-6 bg-gradient-to-r from-primary to-primary/80 rounded-2xl p-6 text-primary-foreground text-center">
              <p className="text-sm font-medium opacity-90 mb-1">
                Economia anual com apenas 1 produto vendido 1x/dia:
              </p>
              <p className="text-3xl lg:text-4xl font-display font-extrabold mb-3">
                R$ 1.383,00
              </p>
              <div className="w-16 h-0.5 bg-primary-foreground/30 mx-auto mb-4" />
              <p className="text-sm lg:text-base font-semibold leading-relaxed max-w-xl mx-auto opacity-95">
                ⚠️ Isso é com <u>UM ÚNICO produto</u>. Imagine seu cardápio com 20, 50 ou 100 itens.
                <br />
                <span className="text-primary-foreground/80 text-xs">
                  Só de comissão, marketplaces podem consumir <strong>R$ 30.000+/ano</strong> do seu faturamento.
                </span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14">
            
            <span className="inline-block text-xs font-bold text-primary uppercase tracking-widest mb-3">Como funciona</span>
            <h2 className="text-2xl lg:text-4xl font-display font-extrabold">
              3 passos para começar a vender
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {STEPS.map((step, i) =>
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              className="relative text-center">
              
                {i < STEPS.length - 1 &&
              <div className="hidden md:block absolute top-8 left-[60%] w-[80%] border-t-2 border-dashed border-primary/20" />
              }
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 border-2 border-primary/20 mb-5">
                  <span className="text-2xl font-display font-extrabold text-primary">{step.num}</span>
                </div>
                <h3 className="font-display font-bold text-lg mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* ── Benefits Grid ── */}
      <section className="py-16 lg:py-20 bg-secondary/30">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12">
            
            <span className="inline-block text-xs font-bold text-primary uppercase tracking-widest mb-3">Funcionalidades</span>
            <h2 className="text-2xl lg:text-4xl font-display font-extrabold mb-3">
              Tudo que seu restaurante precisa
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Um sistema completo para gerenciar pedidos, cardápio, finanças e clientes
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b, i) =>
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="group bg-card border border-border rounded-xl p-6 hover:border-primary/30 hover:shadow-lg transition-all duration-300">
              
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/15 transition-colors">
                  <b.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-display font-bold text-base mb-2">{b.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12">
            
            <span className="inline-block text-xs font-bold text-primary uppercase tracking-widest mb-3">Depoimentos</span>
            <h2 className="text-2xl lg:text-4xl font-display font-extrabold mb-3">
              Quem usa, recomenda
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) =>
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-card border border-border rounded-2xl p-6 relative">
              
                {/* Quote mark */}
                <div className="absolute -top-3 left-6 text-4xl font-serif text-primary/20 leading-none">"</div>

                <div className="flex gap-0.5 mb-4 mt-2">
                  {Array.from({ length: 5 }).map((_, j) =>
                <Star key={j} className="h-4 w-4 fill-primary text-primary" />
                )}
                </div>
                <p className="text-sm text-muted-foreground mb-5 leading-relaxed italic">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="text-xs font-bold text-primary">{t.avatar}</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.biz}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-accent" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.1),transparent_60%)]" />

        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}>
            
            <div className="inline-flex items-center gap-2 bg-primary-foreground/10 border border-primary-foreground/20 text-primary-foreground text-sm font-semibold px-4 py-2 rounded-full mb-8 backdrop-blur-sm">
              <TrendingUp className="h-4 w-4" />
              Junte-se a mais de 500 restaurantes
            </div>

            <h2 className="text-3xl lg:text-5xl font-display font-extrabold text-primary-foreground mb-5 leading-tight">
              Pronto para parar de perder dinheiro com taxas?
            </h2>
            <p className="text-lg text-primary-foreground/80 mb-10 max-w-xl mx-auto">
              Comece seu teste grátis de 30 dias agora. Sem compromisso, sem cartão de crédito.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="h-14 px-10 text-base font-bold bg-primary-foreground text-primary hover:bg-primary-foreground/90 shadow-xl"
                onClick={scrollToForm}>
                
                Começar grátis agora
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-14 px-10 text-base font-semibold border-2 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 backdrop-blur-sm"
                onClick={openWhatsApp}>
                
                <MessageCircle className="mr-2 h-5 w-5" />
                Falar com consultor
              </Button>
            </div>

            {/* Trust row */}
            <div className="flex flex-wrap justify-center gap-6 mt-10">
              {["30 dias grátis", "Sem cartão", "Cancele quando quiser"].map((t) =>
              <div key={t} className="flex items-center gap-1.5 text-sm text-primary-foreground/70">
                  <CheckCircle className="h-4 w-4" />
                  {t}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-8 border-t border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <span>© {new Date().getFullYear()} {config.site_name} — Todos os direitos reservados</span>
          <div className="flex gap-6">
            <a href="/termos" className="hover:text-foreground transition-colors">Termos de Uso</a>
            <a href="/privacidade" className="hover:text-foreground transition-colors">Política de Privacidade</a>
            <a href="/contato" className="hover:text-foreground transition-colors">Contato</a>
          </div>
        </div>
      </footer>

      {/* ── Floating WhatsApp Button ── */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 2, type: "spring" }}
        onClick={openWhatsApp}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-xl flex items-center justify-center hover:scale-110 transition-transform"
        aria-label="WhatsApp">
        
        <MessageCircle className="h-6 w-6" />
      </motion.button>
    </div>);

};

export default LandingAds;